import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, BehaviorSubject, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { switchMap, tap, catchError } from 'rxjs/operators';
import { UserService } from './user.service';
import { Router, ActivatedRoute } from '@angular/router';
import { SubscriptionPlanDTO } from './subscription-plan.service';

/** Subscription details returned with GetCondominium (plan fields plus optional scheduled deactivation). */
export interface CondominiumSubscriptionInfo extends SubscriptionPlanDTO {
  deactivationDate?: string;
}

export interface Condominium {
  id: number;
  name: string;
  address: {
    street: string;
    number: string;
    complement?: string;
    neighborhood?: string;
    city: string;
    state: string;
    country: string;
    zipCode?: string;
  };
  owner: {
    username: string;
  };
  cnpj?: string;
  email?: string;
  phoneNumbers?: string[];
  subscriptionType?: CondominiumSubscriptionInfo;
}

export interface CancelSubscriptionResponse {
  endDate: string;
}

export interface ChangeSubscriptionRequest {
  subscriptionPlanId: number;
}

export interface CondominiumPaymentDTO {
  paymentId?: string;
  createDate?: string;
  asaasCustomerId?: string;
  asaasSubscriptionId?: string;
  paymentLink?: string;
  dueDate?: string;
  status?: string;
  transactionReceiptUrl?: string;
  paymentDate?: string;
  value?: number;
}

export interface PageCondominiumPaymentDTO {
  totalPages?: number;
  totalElements?: number;
  size?: number;
  content?: CondominiumPaymentDTO[];
  number?: number;
  first?: boolean;
  last?: boolean;
  numberOfElements?: number;
  empty?: boolean;
}

export interface CreateCondominiumRequest {
  name: string;
  address: {
    street: string;
    number: string;
    complement?: string;
    neighborhood?: string;
    city: string;
    state: string;
    country: string;
    zipCode?: string;
  };
  subscriptionPlan?: number;
  cnpj?: string;
  email?: string;
  phoneNumbers?: string[];
  acceptedTermsOfUse?: boolean;
  acceptedPrivacyPolicy?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CondominiumService {
  private currentCondominium: Condominium | null = null;
  private selectedCondominiumId = new BehaviorSubject<number>(0);

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  getCondominiums(): Observable<Condominium[]> {
    return this.authService.getPKCEToken().pipe(
      switchMap(token => {
        return this.http.get<Condominium[]>(`${environment.apiUrl}/condominium`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      })
    );
  }

  getCondominiumDetails(id: number): Observable<Condominium> {
    return this.authService.getPKCEToken().pipe(
      switchMap(token => {
        return this.http.get<Condominium>(`${environment.apiUrl}/condominium/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      }),
      tap(condominium => {
        this.currentCondominium = condominium;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          this.router.navigate(['/condominiums']);
        }
        return throwError(error);
      })
    );
  }

  getCondominiumMembers(condominiumId: number): Observable<{ name: string; email: string }[]> {
    return this.authService.getPKCEToken().pipe(
      switchMap(token => {
        return this.http.get<{ name: string; email: string }[]>(`${environment.apiUrl}/condominium/${condominiumId}/members`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      })
    );
  }

  createCondominium(request: CreateCondominiumRequest): Observable<Condominium> {
    // Body mapeado conforme o schema CreateCondominiumRequest do swagger.json
    const body: CreateCondominiumRequest = {
      ...request,
      acceptedTermsOfUse: request.acceptedTermsOfUse ?? false,
      acceptedPrivacyPolicy: request.acceptedPrivacyPolicy ?? false
    };
    return this.authService.getPKCEToken().pipe(
      switchMap(token => {
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
        return this.http.post<Condominium>(`${environment.apiUrl}/condominium`, body, {
          headers: headers
        });
      })
    );
  }

  updateCondominium(id: number, request: CreateCondominiumRequest): Observable<Condominium> {
    // A edição não registra aceite: garante que os campos não sejam enviados.
    const { acceptedTermsOfUse, acceptedPrivacyPolicy, ...body } = request;
    return this.authService.getPKCEToken().pipe(
      switchMap(token => {
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
        return this.http.patch<Condominium>(`${environment.apiUrl}/condominium/${id}`, body, {
          headers: headers
        });
      })
    );
  }

  changeOwner(condominiumId: number, newOwnerEmail: string): Observable<void> {
    return this.authService.getPKCEToken().pipe(
      switchMap(token => {
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
        const body = { email: newOwnerEmail };
        return this.http.put<void>(`${environment.apiUrl}/condominium/${condominiumId}/owner`, body, { headers });
      })
    );
  }

  cancelSubscription(condominiumId: number): Observable<CancelSubscriptionResponse> {
    return this.authService.getPKCEToken().pipe(
      switchMap(token => {
        return this.http.delete<CancelSubscriptionResponse>(
          `${environment.apiUrl}/condominium/${condominiumId}/subscription`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
      })
    );
  }

  changeSubscription(condominiumId: number, body: ChangeSubscriptionRequest): Observable<Condominium> {
    return this.authService.getPKCEToken().pipe(
      switchMap(token => {
        return this.http.post<Condominium>(
          `${environment.apiUrl}/condominium/${condominiumId}/subscription`,
          body,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      })
    );
  }

  getCondominiumPayments(
    condominiumId: number,
    page: number,
    pageSize: number
  ): Observable<PageCondominiumPaymentDTO> {
    return this.authService.getPKCEToken().pipe(
      switchMap(token => {
        return this.http.get<PageCondominiumPaymentDTO>(
          `${environment.apiUrl}/condominium/${condominiumId}/payments`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            },
            params: {
              page: String(page),
              pageSize: String(pageSize)
            }
          }
        );
      })
    );
  }

  async isCurrentUserOwnerOfCondominium(condominiumId: number): Promise<boolean> {
    let currentUsername: string | null | undefined;
    await this.userService.getCurrentUser()?.then(user => {
      currentUsername = user?.username;
    });
    
    let isOwner = false;
    await this.getCondominiumDetails(condominiumId).toPromise()
      .then(condominium => {
        isOwner = condominium?.owner.username === currentUsername;
      })
      .catch(() => {
        isOwner = false;
      });

    return isOwner;
  }

} 